import { notification } from '@surecompanies/core_components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import client from 'enrollment-platform/api/eps.client'
import {
  ActivityLogAction,
  postActivityLog,
} from 'modules/activity-log-service'
import { IntegrationFormValues } from '../component/IntegrationForm/integration.schema'
import { CustomPayrollConfigurationFormValues } from '../component/CustomPayrollConfigurationForm/CustomPayrollConfigurationForm'
import { MedicareColumnsFormValues } from '../component/MedicareColumnsForm/medicareColumns.schema'

// The API Integration Save omits the Medicare pair, which EPS reads as "keep the stored one".
type HrisPayrollRequest = IntegrationFormValues &
  Partial<MedicareColumnsFormValues> &
  Partial<CustomPayrollConfigurationFormValues>

export default function useHrisPayroll(id: string, companyName?: string) {
  const queryClient = useQueryClient()

  async function getHrisPayroll(id: string) {
    const { data } = await client.get(`/customers/${id}/configuration/hris`)
    return data
  }

  async function updateHrisPayroll(values: HrisPayrollRequest) {
    const requestBody = {
      ...values,
      eligibilityRules: data?.eligibilityRules ?? '',
    }

    try {
      const { data: responseData } = await client.put(
        `/customers/${id}/configuration/hris`,
        requestBody
      )
      notification.success({
        message: 'HRIS integration updated successfully',
      })
      postActivityLog({
        action: ActivityLogAction.UPDATE_HRIS_PAYROLL_CONFIGURATION,
        description: `updated HRIS integration for customer ${companyName ?? id}`,
        targetUserId: id,
        targetName: companyName,
        targetType: 'COMPANY',
        additionalData: {
          currentCompany: {
            id,
            name: companyName,
          },
          values: requestBody,
        },
      })
      return responseData
    } catch (error) {
      notification.error({
        message: 'Failed to update HRIS integration',
      })
      throw error
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['hrisPayroll', id],
    queryFn: () => getHrisPayroll(id),
  })

  const {
    mutateAsync: updateHrisIntegrationMutation,
    isPending: isUpdatingHrisIntegration,
  } = useMutation({
    mutationFn: updateHrisPayroll,
    mutationKey: ['updateHrisPayroll', id],
    // Refetch instead of caching the PUT response: it lacks isCMv2APIEnabled, payroll history and the capability flag.
    // Merge the saved values first so the other section's Save never resends pre-save values if the refetch fails.
    onSuccess: (_response, savedValues) => {
      queryClient.setQueryData(
        ['hrisPayroll', id],
        (cached: typeof data) => cached && { ...cached, ...savedValues }
      )
      return queryClient.invalidateQueries({ queryKey: ['hrisPayroll', id] })
    },
  })

  // The Payroll Configuration Format Save resends the stored HRIS fields, so only the pair changes.
  // Read the cache, not the render's `data`, so a click right after another Save sees the merged values.
  const updateMedicareColumns = (pair: MedicareColumnsFormValues) => {
    const stored = queryClient.getQueryData<typeof data>(['hrisPayroll', id])
    return updateHrisIntegrationMutation({
      isCensusSyncEnabled: Boolean(stored?.isCensusSyncEnabled),
      isHRISNotificationDisabled: Boolean(stored?.isHRISNotificationDisabled),
      automaticStopCoverage: Boolean(stored?.automaticStopCoverage),
      webHookUrl: stored?.webHookUrl ?? '',
      ...pair,
    })
  }

  // Same shape as the Medicare Save: resend the stored HRIS fields so only this setting changes.
  const updateCustomPayrollConfiguration = (
    values: CustomPayrollConfigurationFormValues
  ) => {
    const stored = queryClient.getQueryData<typeof data>(['hrisPayroll', id])
    return updateHrisIntegrationMutation({
      isCensusSyncEnabled: Boolean(stored?.isCensusSyncEnabled),
      isHRISNotificationDisabled: Boolean(stored?.isHRISNotificationDisabled),
      automaticStopCoverage: Boolean(stored?.automaticStopCoverage),
      webHookUrl: stored?.webHookUrl ?? '',
      ...values,
    })
  }

  return {
    data,
    isLoading,
    updateHrisIntegration: updateHrisIntegrationMutation,
    updateMedicareColumns,
    updateCustomPayrollConfiguration,
    isUpdatingHrisIntegration,
  }
}
